import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Package, LogOut, RefreshCw, Clock, CheckCircle, 
  Truck, AlertCircle, ChevronRight, Calendar, MapPin, 
  FileText, Loader2, X, Wallet, CreditCard, DollarSign,
  PackageCheck, Send, CheckCircle2, Circle, Ban, Factory, Play,
  ChevronLeft, Hourglass, Camera, Upload as UploadIcon, Printer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VendorPortalDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [vendorSession, setVendorSession] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [ledger, setLedger] = useState({ payments: [], summary: {} });
  const [selectedPO, setSelectedPO] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('orders');
  const [actionLoading, setActionLoading] = useState(null);
  // Date filter (inclusive). Empty means "no bound on this side".
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Server-side pagination state.
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, page_size: 20 });
  // Upload-proof state for the detail dialog.
  const [uploadingProof, setUploadingProof] = useState(false);

  useEffect(() => {
    const session = localStorage.getItem('vendor_session');
    if (!session) {
      navigate('/vendor-portal');
      return;
    }
    const parsed = JSON.parse(session);
    setVendorSession(parsed);
    fetchData(parsed.session_token);

    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/vendor-manifest.json';
    }

    return () => {
      if (manifestLink) {
        manifestLink.href = '/manifest.json';
      }
    };
  }, [navigate]);

  const fetchData = async (token, opts = {}) => {
    try {
      setLoading(true);
      const poParams = new URLSearchParams();
      poParams.set('page', String(opts.page ?? page));
      poParams.set('page_size', String(pageSize));
      const fd = opts.fromDate ?? fromDate;
      const td = opts.toDate ?? toDate;
      if (fd) poParams.set('from_date', fd);
      if (td) poParams.set('to_date', td);

      const [posRes, ledgerRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/enterprise/vendor-portal/purchase-orders?${poParams.toString()}`, {
          headers: { 'X-Vendor-Session': token }
        }),
        axios.get(`${API_BASE_URL}/enterprise/vendor-portal/ledger`, {
          headers: { 'X-Vendor-Session': token }
        })
      ]);
      setPurchaseOrders(posRes.data.purchase_orders || []);
      setPagination(posRes.data.pagination || { page: 1, pages: 1, total: 0, page_size: pageSize });
      setLedger(ledgerRes.data || { payments: [], summary: {} });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData(vendorSession.session_token);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleLogout = () => {
    localStorage.removeItem('vendor_session');
    navigate('/vendor-portal');
  };

  const handleStartManufacturing = async (poId, e) => {
    e?.stopPropagation();
    if (!confirm('Accept this purchase order? You will be able to upload proof images and mark it ready once accepted.')) return;
    
    setActionLoading(poId);
    try {
      await axios.post(
        `${API_BASE_URL}/enterprise/vendor-portal/po/${poId}/start-manufacturing`,
        {},
        { headers: { 'X-Vendor-Session': vendorSession.session_token } }
      );
      toast.success('Order accepted');
      await fetchData(vendorSession.session_token);
      setViewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PO');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkReady = async (poId, e) => {
    e?.stopPropagation();
    if (!confirm('Mark this PO as ready for pickup/delivery?')) return;
    
    setActionLoading(poId);
    try {
      await axios.post(
        `${API_BASE_URL}/enterprise/vendor-portal/po/${poId}/mark-ready`,
        {},
        { headers: { 'X-Vendor-Session': vendorSession.session_token } }
      );
      toast.success('PO marked as ready');
      await fetchData(vendorSession.session_token);
      setViewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PO');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkDelivered = async (poId, e) => {
    e?.stopPropagation();
    if (!confirm('Confirm that this order has been delivered?')) return;
    
    setActionLoading(poId);
    try {
      await axios.post(
        `${API_BASE_URL}/enterprise/vendor-portal/po/${poId}/mark-delivered`,
        {},
        { headers: { 'X-Vendor-Session': vendorSession.session_token } }
      );
      toast.success('PO marked as delivered');
      await fetchData(vendorSession.session_token);
      setViewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PO');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      'pending': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'confirmed': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'manufacturing': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'ready': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'in_transit': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      'delivered': 'bg-green-500/20 text-green-400 border-green-500/30',
      'completed': 'bg-green-500/20 text-green-400 border-green-500/30',
      'cancelled': 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || colors['pending'];
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'paid': 'bg-green-500/20 text-green-400 border-green-500/30',
      'partial': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'unpaid': 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || colors['unpaid'];
  };

  const getStatusIcon = (status) => {
    const icons = {
      'draft': Circle,
      'pending': Clock,
      'confirmed': CheckCircle,
      'manufacturing': Factory,
      'ready': PackageCheck,
      'in_transit': Truck,
      'delivered': CheckCircle2,
      'completed': CheckCircle2,
      'cancelled': Ban
    };
    const Icon = icons[status] || Clock;
    return <Icon className="h-3 w-3 sm:h-4 sm:w-4" />;
  };

  // Vendor-facing status labels. The generic `.replace('_', ' ')`
  // produces "Manufacturing" which is wrong for merchants who resell
  // finished goods (no manufacturing step). This map renders the CTAs
  // and status chips in language that works for both manufacturers
  // and resellers. The underlying DB status stays unchanged.
  const VENDOR_STATUS_LABEL = {
    sent: 'New',
    pending: 'New',
    confirmed: 'Accepted',
    manufacturing: 'In Progress',
    ready: 'Ready',
    in_transit: 'In Transit',
    delivered: 'Delivered',
    completed: 'Completed',
    received: 'Received',
    partially_received: 'Partially Received',
    cancelled: 'Cancelled',
    draft: 'Draft',
  };
  const statusLabel = (s) => VENDOR_STATUS_LABEL[s] || (s ? s.replace(/_/g, ' ') : '');

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDuration = (hours) => {
    if (hours == null) return null;
    const h = Number(hours);
    if (!Number.isFinite(h) || h < 0) return null;
    if (h < 1) {
      const mins = Math.max(1, Math.round(h * 60));
      return `${mins}m`;
    }
    const days = Math.floor(h / 24);
    const remH = Math.round(h % 24);
    if (days >= 1 && remH > 0) return `${days}d ${remH}h`;
    if (days >= 1) return `${days}d`;
    return `${Math.round(h)}h`;
  };

  const handleApplyDateFilter = async () => {
    setPage(1);
    await fetchData(vendorSession.session_token, { page: 1 });
  };

  const handleClearDateFilter = async () => {
    setFromDate('');
    setToDate('');
    setPage(1);
    await fetchData(vendorSession.session_token, { page: 1, fromDate: '', toDate: '' });
  };

  const handlePageChange = async (nextPage) => {
    setPage(nextPage);
    await fetchData(vendorSession.session_token, { page: nextPage });
  };

  const handleUploadProof = async (poId, file) => {
    if (!file) return;
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API_BASE_URL}/enterprise/vendor-portal/po/${poId}/ready-image`,
        formData,
        {
          headers: {
            'X-Vendor-Session': vendorSession.session_token,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const newUrl = res.data?.file_url;
      // Optimistically update the in-memory PO + selectedPO so the
      // dialog reflects the new image without a full refetch.
      setPurchaseOrders((list) => list.map((p) => p.id === poId
        ? { ...p, vendor_ready_images: [...(p.vendor_ready_images || []), newUrl] }
        : p));
      setSelectedPO((cur) => cur && cur.id === poId
        ? { ...cur, vendor_ready_images: [...(cur.vendor_ready_images || []), newUrl] }
        : cur);
      toast.success('Proof image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleRemoveProof = async (poId, fileUrl) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/enterprise/vendor-portal/po/${poId}/ready-image`,
        {
          headers: { 'X-Vendor-Session': vendorSession.session_token },
          data: { file_url: fileUrl },
        }
      );
      setPurchaseOrders((list) => list.map((p) => p.id === poId
        ? { ...p, vendor_ready_images: (p.vendor_ready_images || []).filter((u) => u !== fileUrl) }
        : p));
      setSelectedPO((cur) => cur && cur.id === poId
        ? { ...cur, vendor_ready_images: (cur.vendor_ready_images || []).filter((u) => u !== fileUrl) }
        : cur);
      toast.success('Image removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove image');
    }
  };

  const resolveImageUrl = (url) => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
  };

  const filteredPOs = filterStatus === 'all' 
    ? purchaseOrders 
    : purchaseOrders.filter(po => {
        // IMPORTANT: keep these lists aligned with the `stats` buckets
        // below + with the backend `get_vendor_portal_pos` shape. If
        // the stats badge says "5 Active" but this filter hides the
        // `manufacturing` rows, the vendor can't reach the Mark-Ready
        // button and reports "the button is missing". Same story for
        // `sent` — admins send POs in `sent` status, so the vendor
        // MUST see them under Pending to click Start Manufacturing.
        if (filterStatus === 'pending') return ['draft', 'pending', 'confirmed', 'sent'].includes(po.status);
        if (filterStatus === 'active') return ['manufacturing', 'ready', 'in_transit'].includes(po.status);
        if (filterStatus === 'completed') return ['delivered', 'completed', 'received', 'partially_received'].includes(po.status);
        return po.status === filterStatus;
      });

  const stats = {
    total: pagination.total ?? purchaseOrders.length,
    pending: purchaseOrders.filter(po => ['draft', 'pending', 'confirmed', 'sent'].includes(po.status)).length,
    active: purchaseOrders.filter(po => ['manufacturing', 'ready', 'in_transit'].includes(po.status)).length,
    completed: purchaseOrders.filter(po => ['delivered', 'completed', 'received', 'partially_received'].includes(po.status)).length,
    totalValue: purchaseOrders.reduce((sum, po) => sum + (po.total_amount || 0), 0)
  };

  // Check if vendor can take action on a PO.
  // `sent` is the status admins use when they release a PO to the
  // vendor — it MUST be actionable or the vendor sees the row but no
  // button (the exact complaint we're fixing).
  const canStartManufacturing = (po) => ['pending', 'confirmed', 'sent'].includes(po.status);
  const canMarkReady = (po) => po.status === 'manufacturing';
  const canMarkDelivered = (po) => po.status === 'ready' && po.fulfillment_method === 'vendor_delivers';
  const hasProofImage = (po) => Array.isArray(po?.vendor_ready_images) && po.vendor_ready_images.length > 0;

  if (!vendorSession) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 safe-area-inset">
      {/* Header */}
      <header className="bg-gray-800/80 backdrop-blur-md border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="p-1.5 sm:p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 flex-shrink-0">
                <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-amber-400" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-lg font-semibold text-white truncate">
                  {vendorSession.vendor_name}
                </h1>
                <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">Vendor Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="h-9 w-9 sm:h-10 sm:w-auto p-0 sm:px-3 text-gray-300 hover:bg-gray-700"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline ml-2">Refresh</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="h-9 w-9 sm:h-10 sm:w-auto p-0 sm:px-3 text-gray-400 hover:text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6 pb-20">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-800/50 border border-gray-700 w-full grid grid-cols-2">
            <TabsTrigger 
              value="orders" 
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"
            >
              <Package className="h-4 w-4 mr-2" />
              Purchase Orders
            </TabsTrigger>
            <TabsTrigger 
              value="ledger"
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"
            >
              <Wallet className="h-4 w-4 mr-2" />
              Ledger
            </TabsTrigger>
          </TabsList>

          {/* Purchase Orders Tab */}
          <TabsContent value="orders" className="mt-4 space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-4">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setFilterStatus('all')}
                className={`bg-gray-800/50 border rounded-xl p-3 sm:p-4 cursor-pointer transition-all ${
                  filterStatus === 'all' ? 'border-amber-500' : 'border-gray-700'
                }`}
              >
                <div className="flex items-center gap-1.5 sm:gap-2 text-gray-400 mb-1">
                  <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Total POs</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-white">{stats.total}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                onClick={() => setFilterStatus('pending')}
                className={`bg-gray-800/50 border rounded-xl p-3 sm:p-4 cursor-pointer transition-all ${
                  filterStatus === 'pending' ? 'border-yellow-500' : 'border-gray-700'
                }`}
              >
                <div className="flex items-center gap-1.5 sm:gap-2 text-yellow-400 mb-1">
                  <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Pending</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-white">{stats.pending}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onClick={() => setFilterStatus('completed')}
                className={`bg-gray-800/50 border rounded-xl p-3 sm:p-4 cursor-pointer transition-all ${
                  filterStatus === 'completed' ? 'border-green-500' : 'border-gray-700'
                }`}
              >
                <div className="flex items-center gap-1.5 sm:gap-2 text-green-400 mb-1">
                  <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Completed</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-white">{stats.completed}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 sm:p-4"
              >
                <div className="flex items-center gap-1.5 sm:gap-2 text-amber-400 mb-1">
                  <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Total Value</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold text-white truncate">
                  {formatCurrency(stats.totalValue)}
                </p>
              </motion.div>
            </div>

            {/* Date Filter */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="vendor-po-date-filter">
              <div className="flex-1 min-w-0">
                <label className="text-xs text-gray-400 block mb-1">From</label>
                <DateInput
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  max={toDate || undefined}
                  className="w-full bg-gray-900/60 border border-gray-700 rounded-lg px-3 h-10 text-sm text-white focus:outline-none focus:border-amber-500"
                  data-testid="vendor-po-from-date"
                />
              </div>
              <div className="flex-1 min-w-0">
                <label className="text-xs text-gray-400 block mb-1">To</label>
                <DateInput
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  min={fromDate || undefined}
                  className="w-full bg-gray-900/60 border border-gray-700 rounded-lg px-3 h-10 text-sm text-white focus:outline-none focus:border-amber-500"
                  data-testid="vendor-po-to-date"
                />
              </div>
              <div className="flex gap-2 sm:flex-shrink-0">
                <Button
                  size="sm"
                  onClick={handleApplyDateFilter}
                  className="h-10 bg-amber-500 hover:bg-amber-400 text-black font-medium px-4"
                  data-testid="vendor-po-apply-filter"
                >
                  Apply
                </Button>
                {(fromDate || toDate) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleClearDateFilter}
                    className="h-10 text-gray-300 hover:bg-gray-700"
                    data-testid="vendor-po-clear-filter"
                  >
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Purchase Orders List */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
              <div className="p-3 sm:p-4 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-semibold text-white">
                  Purchase Orders
                  {filterStatus !== 'all' && (
                    <span className="text-sm text-gray-400 ml-2">({filteredPOs.length})</span>
                  )}
                </h2>
                {filterStatus !== 'all' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilterStatus('all')}
                    className="text-gray-400 hover:text-white h-8 px-2"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
                </div>
              ) : filteredPOs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Package className="h-10 w-10 sm:h-12 sm:w-12 mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">No purchase orders found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {filteredPOs.map((po, index) => (
                    <motion.div
                      key={po.id || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="p-3 sm:p-4 hover:bg-gray-700/30 active:bg-gray-700/50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedPO(po);
                        setViewDialogOpen(true);
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <span className="font-semibold text-white text-sm sm:text-base">
                              {po.po_number}
                            </span>
                            <Badge className={`${getStatusColor(po.status)} border text-xs px-1.5 py-0`}>
                              {getStatusIcon(po.status)}
                              <span className="ml-1 capitalize">{statusLabel(po.status)}</span>
                            </Badge>
                            <Badge className={`${getPaymentStatusColor(po.payment_status)} border text-xs px-1.5 py-0`}>
                              <CreditCard className="h-3 w-3 mr-1" />
                              {po.payment_status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400 flex-wrap">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>{formatDate(po.created_at)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Package className="h-3 w-3" />
                              <span>{po.line_items?.length || 0} items</span>
                            </div>
                            {po.fulfillment_method === 'vendor_delivers' && (
                              <div className="flex items-center gap-1 text-purple-400">
                                <Truck className="h-3 w-3" />
                                <span>Delivery</span>
                              </div>
                            )}
                            {po.fulfillment_duration_hours != null && (
                              <div
                                className="flex items-center gap-1 text-emerald-400"
                                data-testid={`vendor-po-fulfilled-in-${po.id}`}
                                title="Time taken from PO creation to ready/delivered"
                              >
                                <Hourglass className="h-3 w-3" />
                                <span>Fulfilled in {formatDuration(po.fulfillment_duration_hours)}</span>
                              </div>
                            )}
                          </div>
                          {/* Quick Actions */}
                          {(canStartManufacturing(po) || canMarkReady(po) || canMarkDelivered(po)) && (
                            <div className="flex gap-2 mt-2">
                              {canStartManufacturing(po) && (
                                <Button
                                  size="sm"
                                  onClick={(e) => handleStartManufacturing(po.id, e)}
                                  disabled={actionLoading === po.id}
                                  className="h-7 text-xs bg-orange-600 hover:bg-orange-700 text-white"
                                >
                                  {actionLoading === po.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Factory className="h-3 w-3 mr-1" />
                                      Accept Order
                                    </>
                                  )}
                                </Button>
                              )}
                              {canMarkReady(po) && (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!hasProofImage(po)) {
                                      setSelectedPO(po);
                                      setViewDialogOpen(true);
                                      toast.message('Upload at least one product image as proof first.');
                                      return;
                                    }
                                    handleMarkReady(po.id, e);
                                  }}
                                  disabled={actionLoading === po.id}
                                  className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                                >
                                  {actionLoading === po.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <>
                                      <PackageCheck className="h-3 w-3 mr-1" />
                                      Mark Ready
                                    </>
                                  )}
                                </Button>
                              )}
                              {canMarkDelivered(po) && (
                                <Button
                                  size="sm"
                                  onClick={(e) => handleMarkDelivered(po.id, e)}
                                  disabled={actionLoading === po.id}
                                  className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                                >
                                  {actionLoading === po.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Send className="h-3 w-3 mr-1" />
                                      Mark Delivered
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-base sm:text-lg font-semibold text-amber-400">
                              {formatCurrency(po.total_amount)}
                            </p>
                          </div>
                          <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div
                  className="flex items-center justify-between gap-3 border-t border-gray-700 px-3 sm:px-4 py-3"
                  data-testid="vendor-po-pagination"
                >
                  <p className="text-xs text-gray-400">
                    Page <span className="text-white font-medium">{pagination.page}</span> of{' '}
                    <span className="text-white font-medium">{pagination.pages}</span>
                    <span className="hidden sm:inline"> · {pagination.total} orders</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePageChange(Math.max(1, page - 1))}
                      disabled={loading || pagination.page <= 1}
                      className="h-8 px-2 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
                      data-testid="vendor-po-prev-page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline ml-1">Prev</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePageChange(Math.min(pagination.pages, page + 1))}
                      disabled={loading || pagination.page >= pagination.pages}
                      className="h-8 px-2 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
                      data-testid="vendor-po-next-page"
                    >
                      <span className="hidden sm:inline mr-1">Next</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Ledger Tab */}
          <TabsContent value="ledger" className="mt-4 space-y-4">
            {/* Ledger Summary */}
            <div className="grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-4">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 sm:p-4"
              >
                <div className="flex items-center gap-1.5 sm:gap-2 text-gray-400 mb-1">
                  <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Total PO Value</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold text-white">
                  {formatCurrency(ledger.summary?.total_po_value)}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 sm:p-4"
              >
                <div className="flex items-center gap-1.5 sm:gap-2 text-green-400 mb-1">
                  <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Total Paid</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold text-white">
                  {formatCurrency(ledger.summary?.total_paid)}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 sm:p-4"
              >
                <div className="flex items-center gap-1.5 sm:gap-2 text-blue-400 mb-1">
                  <CreditCard className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Advance</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold text-white">
                  {formatCurrency(ledger.summary?.total_advance)}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gray-800/50 border border-amber-500/50 rounded-xl p-3 sm:p-4"
              >
                <div className="flex items-center gap-1.5 sm:gap-2 text-amber-400 mb-1">
                  <Wallet className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Outstanding</span>
                </div>
                <p className="text-lg sm:text-2xl font-bold text-amber-400">
                  {formatCurrency(ledger.summary?.outstanding)}
                </p>
              </motion.div>
            </div>

            {/* Payment History */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
              <div className="p-3 sm:p-4 border-b border-gray-700">
                <h2 className="text-base sm:text-lg font-semibold text-white">Payment History</h2>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
                </div>
              ) : ledger.payments?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Wallet className="h-10 w-10 sm:h-12 sm:w-12 mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">No payment records found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {ledger.payments?.map((payment, index) => (
                    <motion.div
                      key={payment.id || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="p-3 sm:p-4"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={`${
                              payment.entry_type === 'payment' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-blue-500/20 text-blue-400'
                            } text-xs px-1.5 py-0`}>
                              {payment.entry_type === 'payment' ? 'Payment' : 'Advance'}
                            </Badge>
                            {payment.payment_mode && (
                              <span className="text-xs text-gray-500">{payment.payment_mode}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(payment.payment_date || payment.created_at)}</span>
                            {payment.linked_po_number && (
                              <>
                                <span>•</span>
                                <span>{payment.linked_po_number}</span>
                              </>
                            )}
                          </div>
                          {payment.notes && (
                            <p className="text-xs text-gray-500 mt-1 truncate">{payment.notes}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-base sm:text-lg font-semibold text-green-400">
                            +{formatCurrency(payment.amount)}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* PO Detail Dialog - Fully Responsive */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white w-full max-w-[95vw] sm:max-w-lg md:max-w-2xl max-h-[95vh] overflow-hidden p-0 flex flex-col">
          {/* Header */}
          <DialogHeader className="p-4 pb-2 border-b border-gray-700 flex-shrink-0">
            <DialogTitle className="flex flex-col gap-2">
              <span className="text-lg font-bold">{selectedPO?.po_number}</span>
              {selectedPO && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${getStatusColor(selectedPO.status)} border text-xs`}>
                    {getStatusIcon(selectedPO.status)}
                    <span className="ml-1 capitalize">{statusLabel(selectedPO.status)}</span>
                  </Badge>
                  <Badge className={`${getPaymentStatusColor(selectedPO.payment_status)} border text-xs`}>
                    <CreditCard className="h-3 w-3 mr-1" />
                    {selectedPO.payment_status}
                  </Badge>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Scrollable Content */}
          {selectedPO && (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* PO Details Grid */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-gray-700/30 rounded-lg text-sm">
                <div>
                  <p className="text-xs text-gray-400">Created</p>
                  <p className="font-medium">{formatDate(selectedPO.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Delivery</p>
                  <p className="font-medium">{formatDate(selectedPO.expected_delivery_date) || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Method</p>
                  <p className="font-medium flex items-center gap-1">
                    {selectedPO.fulfillment_method === 'vendor_delivers' ? (
                      <>
                        <Truck className="h-3 w-3 text-purple-400" />
                        <span>Vendor Delivery</span>
                      </>
                    ) : (
                      <>
                        <Package className="h-3 w-3 text-blue-400" />
                        <span>Pickup</span>
                      </>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Location</p>
                  <p className="font-medium truncate">{selectedPO.warehouse_location_name || '-'}</p>
                </div>
                {selectedPO.vendor_notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-400">Notes</p>
                    <p className="text-gray-300 text-xs">{selectedPO.vendor_notes}</p>
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Order Items ({selectedPO.line_items?.length || 0})
                </h3>
                <div className="space-y-2">
                  {selectedPO.line_items?.map((item, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-2 bg-gray-700/30 rounded-lg"
                    >
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.product_title}
                          className="w-12 h-12 object-cover rounded-lg bg-gray-600 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Package className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm leading-tight line-clamp-2">
                          {item.product_title}
                        </p>
                        {item.variant_title && (
                          <p className="text-xs text-gray-400 truncate">{item.variant_title}</p>
                        )}
                        <p className="text-xs text-gray-500">SKU: {item.sku || '-'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-amber-400">x{item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fulfilment time + proof images */}
              {(selectedPO.fulfillment_duration_hours != null
                || (selectedPO.vendor_ready_images && selectedPO.vendor_ready_images.length > 0)
                || canMarkReady(selectedPO)) && (
                <div data-testid="vendor-po-proof-section">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Camera className="h-4 w-4" />
                      Product Proof
                    </h3>
                    {selectedPO.fulfillment_duration_hours != null && (
                      <span
                        className="text-xs text-emerald-400 flex items-center gap-1"
                        data-testid="vendor-po-fulfillment-duration"
                      >
                        <Hourglass className="h-3 w-3" />
                        Fulfilled in {formatDuration(selectedPO.fulfillment_duration_hours)}
                      </span>
                    )}
                  </div>

                  {selectedPO.vendor_ready_images && selectedPO.vendor_ready_images.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      {selectedPO.vendor_ready_images.map((url, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={resolveImageUrl(url)}
                            alt={`Proof ${idx + 1}`}
                            className="w-full aspect-square object-cover rounded-lg bg-gray-700 border border-gray-600"
                          />
                          {canMarkReady(selectedPO) && (
                            <button
                              type="button"
                              onClick={() => handleRemoveProof(selectedPO.id, url)}
                              className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`vendor-po-remove-proof-${idx}`}
                              aria-label="Remove image"
                            >
                              <X className="h-3 w-3 text-white" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    canMarkReady(selectedPO) && (
                      <p className="text-xs text-amber-400/80 mb-2">
                        Upload at least one product image as proof before marking the PO ready.
                      </p>
                    )
                  )}

                  {canMarkReady(selectedPO) && (
                    <label
                      htmlFor={`vendor-proof-input-${selectedPO.id}`}
                      className="flex items-center justify-center gap-2 w-full h-10 rounded-lg border border-dashed border-gray-600 hover:border-amber-500 hover:bg-amber-500/5 cursor-pointer text-sm text-gray-300 transition-colors"
                      data-testid="vendor-po-upload-proof-trigger"
                    >
                      {uploadingProof ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                        </>
                      ) : (
                        <>
                          <UploadIcon className="h-4 w-4" /> Add product image
                        </>
                      )}
                      <input
                        id={`vendor-proof-input-${selectedPO.id}`}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        disabled={uploadingProof}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUploadProof(selectedPO.id, f);
                          e.target.value = '';
                        }}
                        data-testid="vendor-po-upload-proof-input"
                      />
                    </label>
                  )}
                </div>
              )}

              {/* Amount Summary */}
              <div className="flex justify-between items-center p-3 bg-gray-700/30 rounded-lg">
                <div>
                  <p className="text-xs text-gray-400">Paid</p>
                  <p className="text-lg font-semibold text-green-400">
                    {formatCurrency(selectedPO.paid_amount || 0)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="text-xl font-bold text-amber-400">
                    {formatCurrency(selectedPO.total_amount)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sticky Action Button at Bottom */}
          {selectedPO && (
            <div className="p-4 border-t border-gray-700 bg-gray-800 flex-shrink-0 space-y-2">
              {canStartManufacturing(selectedPO) && (
                <Button
                  onClick={(e) => handleStartManufacturing(selectedPO.id, e)}
                  disabled={actionLoading === selectedPO.id}
                  className="w-full h-12 bg-orange-600 hover:bg-orange-700 text-white font-semibold text-base"
                >
                  {actionLoading === selectedPO.id ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Factory className="h-5 w-5 mr-2" />
                  )}
                  Accept Order
                </Button>
              )}
              {canMarkReady(selectedPO) && (
                <Button
                  onClick={(e) => handleMarkReady(selectedPO.id, e)}
                  disabled={actionLoading === selectedPO.id || !hasProofImage(selectedPO)}
                  className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold text-base disabled:opacity-50 disabled:hover:bg-purple-600"
                  data-testid="vendor-po-mark-ready-button"
                >
                  {actionLoading === selectedPO.id ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <PackageCheck className="h-5 w-5 mr-2" />
                  )}
                  {hasProofImage(selectedPO) ? 'Mark as Ready' : 'Upload proof image to enable'}
                </Button>
              )}
              {canMarkDelivered(selectedPO) && (
                <Button
                  onClick={(e) => handleMarkDelivered(selectedPO.id, e)}
                  disabled={actionLoading === selectedPO.id}
                  className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-semibold text-base"
                >
                  {actionLoading === selectedPO.id ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Send className="h-5 w-5 mr-2" />
                  )}
                  Mark as Delivered
                </Button>
              )}
              {!['draft', 'cancelled'].includes(selectedPO.status) && (
                <Button
                  onClick={() => window.open(`/vendor-portal/labels/${selectedPO.id}`, '_blank')}
                  variant="outline"
                  className="w-full h-12 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 font-semibold text-base"
                  data-testid="vendor-po-print-labels-button"
                >
                  <Printer className="h-5 w-5 mr-2" />
                  Print Labels (4x6)
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorPortalDashboardPage;
